<template>
  <print v-if="isPrint" :value="form" :header="header" :board="board" />
  <div v-else>
    <div class="required-notice">빨간별표 표시는 필수 입력사항입니다.</div>

    <!-- 저장 확인 팝업 -->
    <vs-popup title="저장" :active.sync="showSaveConfirm">
      <p class="save-confirm-text">
        <span class="save-confirm-highlight">[{{ form.점검연도 }}년, {{ form.구분 }}]</span>
        안전관리 의무사항 이행 자체점검 결과를 저장하시겠습니까?
      </p>
      <div class="flex justify-end gap-3 mt-6">
        <vs-button type="flat" color="grey" @click="onCancelSave">취소</vs-button>
        <vs-button color="secondary" @click="onConfirmSave">저장</vs-button>
      </div>
    </vs-popup>

    <h5 ref="refYeong" class="required mt-28px"><i class="dot"></i> 점검연도 <number-input v-model="form.점검연도" :useComma="false" :align="'left'" style="width: 114px; display: inline-block" class="mx-2" /><span>년</span></h5>

    <!-- 구분 -->
    <div class="mt-28px">
      <h5 class="mb-4 required"><i class="dot"></i> 구분</h5>

      <div ref="refGubun" class="grid grid-cols-6">
        <radio-group v-model="form.구분" flex :items="['상반기', '하반기']" />
      </div>
    </div>

    <!-- ① 총괄 현황 -->
    <div class="section-subtitle mt-28px">① 총괄 현황</div>

    <!--    기본정보-->
    <field-select-module v-model="form.stdInfo" :board="board" :is-print="isPrint" :visible-field-form="false" :useFieldInfo="showWorkplaceInfo" class="mt-28px" />

    <!--    점검시설정보-->
    <div class="mb-4 mt-28px">
      <div>
        <h5><i class="dot"></i>점검시설정보</h5>
      </div>
    </div>
    <div class="">
      <div class="writeGridContainer">
        <div ref="refSiseol" class="writeGridCol2">
          <div class="required">시설명</div>
          <div class="flex flex-1 items-center">
            <div v-if="isPrint" class="print-textarea">{{ form.fields.join(",") }}</div>
            <field-selector class="w-full" multiple v-model="form.fields" @select="selectFields"></field-selector>
          </div>
        </div>
        <div class="writeGridCol2">
          <div class="required">소관부서</div>
          <span class="print-input"> {{ 소관부서 }} </span>
        </div>
      </div>

      <div class="writeGridContainer">
        <div ref="refGigan" class="col-span-6">
          <div class="required">점검기간</div>
          <span v-if="isPrint" class="print-input"> {{ form.점검기간.sDate }} - {{ form.점검기간.eDate }} </span>
          <div v-else class="flex flex-1 items-center">
            <date-selector-rang v-model="form.점검기간" :readonly="board.readonly" />
          </div>
        </div>
      </div>

      <div class="writeGridContainer">
        <div class="writeGridLineItem">
          <span ref="refJeomgum" class="required">점검내용</span>
          <span v-if="isPrint" class="print-input"> {{ form.점검내용 }} </span>
          <ml-input v-else v-model="form.점검내용" :readonly="board.readonly" placeholder="중대재해처벌법 시행에 따른 안전관리 의무이행 사항 자체점검" />
        </div>
        <div class="writeGridLineItem">
          <span ref="refChongpyong" class="required">총평</span>
          <span v-if="isPrint" class="print-input"> {{ form.총평 }} </span>
          <ml-input v-else v-model="form.총평" :readonly="board.readonly" placeholder="전반적인 안전관리 사항, 이행점검 시 중요사항 및 애로사항 중심으로 기재" />
        </div>
      </div>
    </div>

    <!--    시설개요-->
    <div class="mt-28px">
      <div class="flex items-center mb-4">
        <h5 class="required"><i class="dot"></i>시설개요</h5>
        <span class="ml-auto" style="color: #7e72f2">※ ⑥, ⑦번 항목은 위탁시설만 기재</span>
      </div>
      <div class="overflow-y-auto" style="position: relative; max-height: 620px">
        <table cellspacing="0" style="border-spacing: 0">
          <thead style="position: sticky; top: 0; z-index: 10; box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1)">
            <tr>
              <th>No.</th>
              <th>①시설명</th>
              <th>②주소</th>
              <th>③규모</th>
              <th>④대상법령</th>
              <th>⑤시설종류</th>
              <th>⑥관리형태</th>
              <th>⑥위탁시설-위탁부서</th>
              <th>⑥위탁시설-위탁기간</th>
              <th>⑦수탁현황-기관명</th>
              <th>⑦수탁현황-수탁비용</th>
              <th>수정/삭제</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(field, index) in form.fields">
              <td class="text-center">{{ index + 1 }}</td>
              <td class="text-center">{{ field.field_name }}</td>
              <td class="text-center">{{ field.address }}</td>
              <td class="text-center">
                <template v-if="editIndex === index">
                  <ml-input v-model="editData.규모" class="w-full text-left" placeholder="연면적" />
                </template>
                <template v-else>
                  {{ field.규모 }}
                </template>
              </td>
              <td class="text-center">
                <template v-if="editIndex === index">
                  <ml-input v-model="editData.대상법령" class="w-full text-left" placeholder="실내공기질관리법 (시설물 안전법 1,2,3종)" />
                </template>
                <template v-else>
                  {{ field.대상법령 }}
                </template>
              </td>
              <td class="text-center">{{ field.시설종류 }}</td>
              <td class="text-center">{{ field.관리형태 }}</td>
              <td class="text-center">
                <template v-if="editIndex === index">
                  <ml-input v-model="editData.위탁부서" class="w-full text-left" placeholder="입력" />
                </template>
                <template v-else>
                  {{ field.위탁부서 }}
                </template>
              </td>
              <td class="text-center">
                <template v-if="editIndex === index">
                  <date-selector-rang v-model="editData.위탁기간" class="w-full" />
                </template>
                <template v-else>
                  {{ field.위탁기간 && `${field.위탁기간.sDate} ~ ${field.위탁기간.eDate}` }}
                </template>
              </td>
              <td class="text-center">
                <template v-if="editIndex === index">
                  <ml-input v-model="editData.수탁기관명" class="w-full text-left" placeholder="입력" />
                </template>
                <template v-else>
                  {{ field.수탁기관명 }}
                </template>
              </td>
              <td class="text-center">
                <template v-if="editIndex === index">
                  <ml-input v-model="editData.수탁비용" class="w-full text-left" placeholder="00백만원/독립채산제" />
                </template>
                <template v-else>
                  {{ field.수탁비용 }}
                </template>
              </td>
              <td class="text-center">
                <div class="icon-buttons flex justify-center gap-3">
                  <fragment v-if="editIndex === index">
                    <button @click="onClickFinishData(index)">
                      <feather-icon icon="CheckIcon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickCancelData(index)">
                      <feather-icon icon="XIcon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                  <fragment v-else>
                    <button @click="onClickEditData(index)">
                      <feather-icon icon="Edit2Icon" svgClasses="h-5 w-5" />
                    </button>
                    <button @click="onClickRemoveData(index)">
                      <feather-icon icon="Trash2Icon" svgClasses="h-5 w-5" />
                    </button>
                  </fragment>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ② 안전관리 추진현황 -->
    <div class="section-subtitle mt-28px">② 안전관리 추진현황</div>

    <!-- 안전예산 집행내역 -->
    <div ref="refJiphaeng" class="mt-28px">
      <h5 class="mb-4 required flex items-center">
        <i class="dot"></i>안전예산 집행내역
        <span class="ml-2" style="font-size: 14px; color: #ff0000">* 안전예산 집행내역 작성 시 위탁시설과 직영시설은 따로 분리해서 작성</span>
      </h5>

      <!-- 직영시설 테이블 -->
      <div class="flex justify-between items-center mb-2">
        <div class="flex items-center">
          <span style="font-weight: 400">안전예산 집행내역(<span style="color: #4839EB">직영시설</span>)</span>
          <vs-checkbox
            :key="`jiphaeng_none_직영_${jiphaengNoneRenderKey}`"
            :checked="form.집행내역_직영_해당사항없음"
            :disabled="board.readonly"
            @change="onToggleJiphaengNone('직영')"
            class="ml-2"
            >해당사항 없음</vs-checkbox
          >
        </div>
        <div v-if="!form.집행내역_직영_해당사항없음" class="flex gap-2 items-center">
          <span style="font-size: 13px; color: #666">[단위: 천원]</span>
          <vs-button v-if="!board.readonly" size="small" color="secondary" @click="addJiphaengRow('직영')">항목추가</vs-button>
          <vs-button v-if="!board.readonly" size="small" color="danger" type="border" @click="deleteJiphaengRows('직영')">선택삭제</vs-button>
        </div>
      </div>
      <template v-if="!form.집행내역_직영_해당사항없음">
        <table class="mb-2">
          <thead>
            <tr>
              <th v-if="!board.readonly" style="width: 50px"></th>
              <th>구분</th>
              <th>편성액</th>
              <th>집행액</th>
              <th>잔액</th>
              <th>추가소요액</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in form.집행내역_직영" :key="`직영_${idx}`">
              <td v-if="!board.readonly" style="text-align: center">
                <vs-checkbox v-model="row.checked" />
              </td>
              <td>
                <ml-input v-model="row.구분" :readonly="board.readonly" class="w-full" />
              </td>
              <td><div class="flex items-center gap-1"><comma-input v-model="row.편성액" :readonly="board.readonly" @input="recalcJiphaeng('직영')" class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input v-model="row.집행액" :readonly="board.readonly" @input="recalcJiphaeng('직영')" class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="row.잔액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input v-model="row.추가소요액" :readonly="board.readonly" @input="recalcJiphaeng('직영')" class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
            </tr>
            <tr>
              <td v-if="!board.readonly"></td>
              <th>합계</th>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_직영.편성액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_직영.집행액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_직영.잔액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_직영.추가소요액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
            </tr>
          </tbody>
        </table>
        <div style="font-size: 13px; color: #7e72f2" class="mb-2">* 직접관리 시설은 안전관리를 위해 채용한 기간제, 일시사역 인력(공무원·공무직인건비 제외)의 인건비만 기재</div>
        <div class="writeGridContainer mb-6">
          <div class="writeGridLineItem">
            <div class="textarea-label p-0">추가 소요액 확보계획</div>
            <ml-input :readonly="board.readonly" v-model="form.추가소요액확보계획_직영" placeholder="안전점검 추가소요액 해당년도, 추경예산으로 확보" />
          </div>
        </div>
      </template>

      <!-- 위탁시설 테이블 -->
      <div class="flex justify-between items-center mb-2">
        <div class="flex items-center">
          <span style="font-weight: 400">안전예산 집행내역(<span style="color: #E42728">위탁시설</span>)</span>
          <vs-checkbox
            :key="`jiphaeng_none_위탁_${jiphaengNoneRenderKey}`"
            :checked="form.집행내역_위탁_해당사항없음"
            :disabled="board.readonly"
            @change="onToggleJiphaengNone('위탁')"
            class="ml-2"
            >해당사항 없음</vs-checkbox
          >
        </div>
        <div v-if="!form.집행내역_위탁_해당사항없음" class="flex gap-2 items-center">
          <span style="font-size: 13px; color: #666">[단위: 천원]</span>
          <vs-button v-if="!board.readonly" size="small" color="secondary" @click="addJiphaengRow('위탁')">항목추가</vs-button>
          <vs-button v-if="!board.readonly" size="small" color="danger" type="border" @click="deleteJiphaengRows('위탁')">선택삭제</vs-button>
        </div>
      </div>
      <template v-if="!form.집행내역_위탁_해당사항없음">
        <table class="mb-2">
          <thead>
            <tr>
              <th v-if="!board.readonly" style="width: 50px"></th>
              <th>구분</th>
              <th>편성액</th>
              <th>집행액</th>
              <th>잔액</th>
              <th>추가소요액</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in form.집행내역_위탁" :key="`위탁_${idx}`">
              <td v-if="!board.readonly" style="text-align: center">
                <vs-checkbox v-model="row.checked" />
              </td>
              <td>
                <ml-input v-model="row.구분" :readonly="board.readonly" class="w-full" />
              </td>
              <td><div class="flex items-center gap-1"><comma-input v-model="row.편성액" :readonly="board.readonly" @input="recalcJiphaeng('위탁')" class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input v-model="row.집행액" :readonly="board.readonly" @input="recalcJiphaeng('위탁')" class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="row.잔액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input v-model="row.추가소요액" :readonly="board.readonly" @input="recalcJiphaeng('위탁')" class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
            </tr>
            <tr>
              <td v-if="!board.readonly"></td>
              <th>합계</th>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_위탁.편성액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_위탁.집행액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_위탁.잔액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
              <td><div class="flex items-center gap-1"><comma-input :value="form.합계_위탁.추가소요액" readonly class="w-full text-right" /><span class="whitespace-no-wrap">천원</span></div></td>
            </tr>
          </tbody>
        </table>
        <div style="font-size: 13px; color: #7e72f2" class="mb-2">* 위탁관리 시설은 안전관리 전체 인원의 인건비 기재</div>
        <div class="writeGridContainer">
          <div class="writeGridLineItem">
            <div class="textarea-label p-0">추가 소요액 확보계획</div>
            <ml-input :readonly="board.readonly" v-model="form.추가소요액확보계획_위탁" placeholder="안전점검 추가소요액 해당년도, 추경예산으로 확보" />
          </div>
        </div>
      </template>
    </div>

    <div>
      <h5 ref="refInryeok"><i class="dot"></i>안전인력 운용현황</h5>
      <table-custom
        class="pt-0"
        :board="board"
        :is-print="isPrint"
        :title="'<span style=\'font-size: 16px;font-weight: 400;\'><span style=\'color: #ff0000;font-weight: 600;margin-right: 4px;\'>*</span> - 인력운용 총괄</span>'"
        :title-type="'html'"
        :controls="false"
        :multiple="false"
        :tableData="[
          { id: 'cell1', title: '확보인원', type: 'number', placeholder: '숫자입력', width: '188px' },
          { id: 'cell2', title: '주요업무', type: 'text', placeholder: '실제 시설에서 수행하는 업무를 구체적으로 기재', style: { alignText: 'left' } },
          { id: 'cell3', title: '추가필요인력', type: 'number', placeholder: '숫자입력', width: '188px' },
          { id: 'cell4', title: '사유 및 추가확보 계획', type: 'text', placeholder: '소관시설물 증가에 따른 안전점검 업무증가로 조직 및 인사부서와 협의' },
        ]"
        v-model="form.안전인력_운용현황"
      />
      <div class="required" style="font-size: 16px; font-weight: 400; margin-bottom: 14px">- 업무분장 반영 여부</div>
      <div ref="refBuummu" class="grid pb-2 gap-2">
        <vs-radio v-model="form.업무분장_반영여부" :disabled="board.readonly" vs-name="안전인력_운용현황.업무분장_반영여부" vs-value="업무분장 반영">업무분장 반영</vs-radio>
        <div class="flex gap-2 items-center">
          <vs-radio v-model="form.업무분장_반영여부" :disabled="board.readonly" vs-name="안전인력_운용현황.업무분장_반영여부" vs-value="업무분장 미 반영">업무분장 미 반영</vs-radio>
          <ml-input v-model="form.업무분장_반영여부_input" :disabled="form.업무분장_반영여부 === '업무분장 반영'"></ml-input>
          <span>※(예시)00월까지 반영</span>
        </div>
      </div>
    </div>

    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i>안전점검 및 보수보강 등 유지관리 현황 <span style="font-size: 14px; color: #7e72f2">※ 관계법령상 모든 안전점검기재</span></h5>
      <!-- 정기점검 -->
      <table-custom
        ref="refJeonggi"
        class="pt-0"
        :board="board"
        :is-print="isPrint"
        :title="'<span style=\'font-size: 16px;font-weight: 400;\'><span style=\'color: #ff0000;font-weight: 600;margin-right: 4px;\'>*</span>- 정기점검</span>'"
        :title-type="'html'"
        :tableData="[
          { id: 'cell1', title: '관련법령', type: 'text', placeholder: '시설물안전법 제11조' },
          { id: 'cell2', title: '분야', type: 'text', placeholder: '건축정기안전점검' },
          { id: 'cell3', title: '점검(예정)일자', type: 'text', placeholder: '입력' },
          { id: 'cell4', title: '점검내용', type: 'text', placeholder: '건축물 구조, 균열 점검' },
          { id: 'cell5', title: '점검방법(금액)', type: 'text', placeholder: '자체/용역(예산00백만원)' },
        ]"
        v-model="form.유지관리_현황_정기점검"
      />
      <!-- 수시점검(유지관리) - 필수 -->
      <table-custom
        ref="refSusi"
        class="pt-0"
        :board="board"
        :is-print="isPrint"
        :tableData="[
          { id: 'cell1', title: '관련법령', type: 'text', placeholder: '승강기안전관리법 제31조' },
          { id: 'cell2', title: '분야', type: 'text', placeholder: '승강기유지관리' },
          { id: 'cell3', title: '주기', type: 'text', placeholder: '월1회' },
          { id: 'cell4', title: '유지관리사항', type: 'text', placeholder: '승강기 수리 부품교체 등' },
        ]"
        v-model="form.유지관리_현황_수시점검"
      >
        <template #title>
          <div class="flex items-center">
            <span style="font-size: 16px; font-weight: 400"><span style="color: #ff0000; font-weight: 600; margin-right: 4px;">*</span>- 수시점검(유지관리)</span>
          </div>
        </template>
      </table-custom>
      <!-- 정밀안전 점검 및 진단 - 해당사항없음, 항상 표출 -->
      <table-custom
        ref="refJeongmil"
        class="pt-0"
        :board="board"
        :is-print="isPrint"
        :controls="!form.유지관리_현황_정밀점검_해당사항없음"
        :tableData="form.유지관리_현황_정밀점검_해당사항없음 ? [] : [
          { id: 'cell1', title: '관련법령', type: 'text', placeholder: '시설물안전법 제11조' },
          { id: 'cell2', title: '분야', type: 'text', placeholder: '건축정기안전점검' },
          { id: 'cell3', title: '점검(예정)일자', type: 'text', placeholder: '입력' },
          { id: 'cell4', title: '점검내용', type: 'text', placeholder: '건축물 구조, 균열 점검' },
          { id: 'cell5', title: '점검방법(금액)', type: 'text', placeholder: '자체/용역(예산00백만원)' },
        ]"
        v-model="form.유지관리_현황_정밀점검"
      >
        <template #title>
          <div class="flex items-center">
            <span style="font-size: 16px; font-weight: 400">- 정밀안전 점검 및 진단</span>
            <vs-checkbox v-model="form.유지관리_현황_정밀점검_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
          </div>
        </template>
      </table-custom>
    </div>

    <table-custom
      ref="refYuhae"
      :board="board"
      :is-print="isPrint"
      :controls="!form.주요유해위험요인발생및조치현황_해당사항없음"
      :tableData="form.주요유해위험요인발생및조치현황_해당사항없음 ? [] : [
        { id: 'cell1', title: '발생현황', type: 'text', placeholder: '00건물 난관파손' },
        { id: 'cell2', title: '확인경위', type: 'text', placeholder: '신고/점검', width: '188px' },
        { id: 'cell3', title: '조치사항', type: 'text', placeholder: '00~00까지 이용제한, 보수보강 공사진행(00천원)' },
        { id: 'cell4', title: '보고<br>(시장)', titleType: 'html', type: 'radio', middle: true, radioItems: [{ value: '보고' }, { value: '미보고' }], width: '188px' },
      ]"
      v-model="form.주요유해위험요인발생및조치현황"
    >
      <template #title>
        <div class="flex items-center">
          <h5><i class="dot"></i>주요 유해위험 요인 발생 및 조치현황</h5>
          <vs-checkbox v-model="form.주요유해위험요인발생및조치현황_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
        </div>
      </template>
    </table-custom>

    <table-custom
      ref="refBisang"
      :board="board"
      :is-print="isPrint"
      :controls="!form.비상대피훈련실시_해당사항없음"
      :tableData="form.비상대피훈련실시_해당사항없음 ? [] : [
        { id: 'cell1', title: '대상법령', type: 'text', placeholder: '중처법 시행령제10조 제7호' },
        { id: 'cell2', title: '시행여부', type: 'radio', middle: true, radioItems: [{ value: '시행' }, { value: '미시행' }], width: '188px' },
        { id: 'cell3', title: '조치계획', type: 'customType' },
      ]"
      v-model="form.비상대피훈련실시"
    >
      <template #title>
        <div class="flex items-center">
          <h5 class="flex items-center"><i class="dot"></i>비상대피훈련 실시 <span style="font-size: 14px; color: #7e72f2" class="ml-1">※ 1종 시설에 해당됨</span></h5>
          <vs-checkbox v-model="form.비상대피훈련실시_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
        </div>
      </template>
      <template #customType="{ trData, value, input }">
        <input v-if="trData && trData.cell2 === '미시행'" :readonly="board.readonly" class="w-full flex-1" :value="value" @input="(e) => input(e.target.value)" placeholder="00.00월 까지 시행예정" />
      </template>
    </table-custom>

    <table-custom
      ref="refGwangyue"
      :board="board"
      :is-print="isPrint"
      :tableData="[
        { id: 'cell1', title: '의무사항', type: 'text', placeholder: '전기안전관리법제11조에 따른 자가용전기설비 정기 검사, 전기안전관리법 제22조 전기안전관리자 선임 등' },
        { id: 'cell2', title: '이행여부', type: 'radio', middle: true, radioItems: [{ value: '이행' }, { value: '미이행' }], width: '188px' },
        { id: 'cell3', title: '개선계획', type: 'text', placeholder: '00.00까지 선임예정', width: '376px' },
      ]"
      v-model="form.관계법령의의무사항이행현황"
    >
      <template #title>
        <div class="flex items-center">
          <h5 class="flex items-center"><span style="color: #ff0000; font-weight: 600; margin-right: 4px;">*</span><i class="dot"></i>관계법령의 의무사항 이행현황 <span style="font-size: 14px; color: #7e72f2" class="ml-1">※ 안전점검 기재 제외</span></h5>
        </div>
      </template>
    </table-custom>

    <table-custom
      ref="refGyoyuk"
      :board="board"
      :is-print="isPrint"
      :tableData="[
        { id: 'target', title: '교육대상', type: 'text', placeholder: '소방안전관리자, 위험물관리자 등' },
        { id: 'take', title: '이수여부', type: 'radio', middle: true, radioItems: [{ value: '이수' }, { value: '미이수' }], width: '150px' },
        { id: 'plan', title: '이수일/이수계획', type: 'date', placeholder: '날짜선택' },
        { id: 'reason', title: '미이수 사유', type: 'customType' },
        { id: 'note', title: '비고', type: 'text', placeholder: '00.00까지 선임예정' },
      ]"
      v-model="form.교육이수현황"
    >
      <template #title>
        <div class="flex items-center">
          <h5 class="flex items-center"><span style="color: #ff0000; font-weight: 600; margin-right: 4px;">*</span><i class="dot"></i>교육 이수현황</h5>
          <span class="ml-2" style="font-size: 14px">[단위: 명]</span>
          <span class="ml-2" style="font-size: 14px; color: #7e72f2">※ 위탁의 경우 수탁관계자의 교육 이수현황 필히 기재</span>
        </div>
      </template>
      <template #customType="{ trData, value, input }">
        <input v-if="!!trData && !!trData.take && trData.take === '미이수'" :readonly="board.readonly" class="w-full flex-1" :value="value" @input="(e) => input(e.target.value)" placeholder="위험물관리자 변경(선임 6개월이내 이수)" />
      </template>
      <template #customTr>
        <tr style="background: #f9f9f9 !important">
          <td v-if="!isPrint" class="border-none"></td>
          <td style="padding: 10px 15px" colspan="5" class="border-none">
            <div class="flex" style="place-content: center">
              <div class="mx-10" style="align-self: center">합계</div>
              <comma-input readonly placeholder="자동입력" v-model="form.takeSum" />
              <div class="mx-10" style="align-self: center">이수자 수</div>
              <comma-input readonly placeholder="자동입력" v-model="form.taken" />
              <div class="mx-10" style="align-self: center">미이수자 수</div>
              <comma-input readonly placeholder="자동입력" v-model="form.untaken" />
            </div>
          </td>
        </tr>
      </template>
    </table-custom>

    <!-- ③ 관리개선 및 보완사항 -->
    <div class="section-subtitle mt-28px">③ 관리개선 및 보완사항</div>

    <table-custom
      ref="refUimu"
      class="mt-4"
      :board="board"
      :is-print="isPrint"
      :controls="!form.의무사항미이행현황_해당사항없음"
      :tableData="form.의무사항미이행현황_해당사항없음 ? [] : [
        { id: 'cell1', title: '관계법령', type: 'text', placeholder: '입력' },
        { id: 'cell2', title: '의무사항', type: 'text', placeholder: '입력' },
        { id: 'cell3', title: '미조치 사유', type: 'text', placeholder: '입력' },
        { id: 'cell4', title: '이행계획', type: 'text', placeholder: '입력' },
      ]"
      v-model="form.의무사항미이행현황"
    >
      <template #title>
        <div class="flex items-center">
          <h5><i class="dot"></i>의무사항 미이행 현황</h5>
          <vs-checkbox v-model="form.의무사항미이행현황_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
        </div>
      </template>
    </table-custom>

    <div ref="refSago" class="mt-28px">
      <div class="flex justify-between items-center mb-4">
        <div class="flex items-center">
          <h5><i class="dot"></i>사고발생 현황</h5>
          <vs-checkbox v-model="form.사고발생현황_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
        </div>
        <span>[단위: 명]</span>
      </div>
      <table v-if="!form.사고발생현황_해당사항없음" class="mb-4">
        <tbody>
          <tr>
            <th>구분-피해현황</th>
            <th style="border-right: none">피해인원</th>
            <th style="border-left: none; border-right: none">사고경위 및 조치</th>
            <th style="border-left: none; border-right: none">비고</th>
          </tr>
          <tr v-for="cell in form.사고발생현황">
            <th>{{ cell.cell1 }}</th>
            <td style="border-right: none">
              <p v-if="isPrint">{{ cell.cell2 }}</p>
              <ml-input v-else class="w-full" v-model="cell.cell2" :readonly="cell.cell1 === '계'" type="number" :min-value="0" />
            </td>
            <td style="border-left: none; border-right: none">
              <p v-if="isPrint">{{ cell.cell3 }}</p>
              <ml-input v-else class="w-full" v-model="cell.cell3" />
            </td>
            <td style="border-left: none; border-right: none">
              <p v-if="isPrint">{{ cell.cell4 }}</p>
              <ml-input v-else class="w-full" v-model="cell.cell4" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div ref="refGita" class="mt-28px">
      <div class="flex items-center mb-2">
        <h5><i class="dot"></i>기타 개선 및 보완이 필요한 사항</h5>
        <vs-checkbox v-model="form.기타개선및보완이필요한사항_해당사항없음" :disabled="board.readonly" class="ml-2">해당사항 없음</vs-checkbox>
      </div>
      <template v-if="!form.기타개선및보완이필요한사항_해당사항없음">
        <div class="mb-2">- 점검결과(총평)</div>
        <div class="w-full">
          <p v-if="isPrint">
            {{ form.기타개선및보완이필요한사항 }}
          </p>
          <ml-textarea
            class="m-0"
            :readonly="board.readonly"
            v-else
            rows="5"
            v-model="form.기타개선및보완이필요한사항"
            placeholder="※ 보완해야 할 내용, 미흡·부족 사항 보완시기
※ 기타: 문제점·애로사항 및 재도개선·발전방안 건의 등"
          />
        </div>
      </template>
    </div>

    <!-- 의무이행사항 점검결과 -->
    <div ref="refInspect" class="mt-28px">
      <h5 class="mb-4 required"><i class="dot"></i> 의무이행사항 점검결과</h5>
      <div class="grid grid-cols-4 gap-x-6 gap-y-4 mt-4">
        <div v-for="(_title, _title_i) in getTitles" :key="`checkbox_inspect_${_title}`" class="col-span-1 flex items-center">
          <div v-if="isPrint || board.readonly" class="flex items-center">
            <div v-if="form.inspectChecked.includes(_title)" style="background: #200c63" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
              <vs-icon icon="check" size="x-small"></vs-icon>
            </div>
            <div v-else style="border: 2px solid rgb(180, 180, 180)" class="w-5 h-5 rounded-sm mr-2"></div>
            <span class="flex-1">
              {{ _title }}
            </span>
          </div>
          <div v-else>
            <span class="flex">
              <vs-checkbox :key="`inspect_${_title}_${inspectCheckRenderKey}`" :checked="form.inspectChecked.includes(_title)" @change="onToggleInspectChecked(_title)">{{ _title }}</vs-checkbox>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!--    의무이행사항 점검결과(원료/제조물) 체크시 -->
    <TableInspectMerge
      v-for="(item, itemIndex) in form.inspectList"
      :key="`inspect1_${item.title}_${itemIndex}`"
      v-show="form.inspectChecked.includes(item.title) && item.title === '의무이행사항 점검결과(원료/제조물)'"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      :title="item.title"
      :is-title-dot="item.isTitleDot"
      :tableHeaderTitle="item.tableHeaderTitle || '중대재해처벌법 의무이행 사항'"
      :tableHeaderCheck="item.tableHeaderCheck"
      :tableHeaderNote="item.tableHeaderNote"
      :footer="item.footer"
      v-model="item.list"
      class="mt-28px"
    />

    <!--    의무이행사항 점검결과(공중이용시설 직접관리 시설) 체크시-->
    <TableInspectMerge
      v-for="(item, itemIndex) in form.inspectList"
      :key="`inspect2_${item.title}_${itemIndex}`"
      v-show="form.inspectChecked.includes(item.title) && item.title === '의무이행사항 점검결과(공중이용시설 직접관리 시설)'"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      :title="item.title"
      :is-title-dot="item.isTitleDot"
      :tableHeaderTitle="item.tableHeaderTitle || '중대재해처벌법 의무이행 사항'"
      :tableHeaderCheck="item.tableHeaderCheck"
      :tableHeaderNote="item.tableHeaderNote"
      :footer="item.footer"
      v-model="item.list"
      class="mt-28px"
      @click="onClickInspectList"
    />

    <!--    의무이행사항 점검결과(공중이용시설 위탁운영 시설) 체크시-->
    <h5 v-if="form.inspectChecked.includes('의무이행사항 점검결과(공중이용시설 위탁운영 시설)')" class="mt-28px"><i class="dot"></i>의무이행사항 점검결과(위탁운영 시설)</h5>
    <TableInspectMerge
      v-for="(item, itemIndex) in form.inspectList"
      :key="`inspect3_${item.title}_${itemIndex}`"
      v-show="form.inspectChecked.includes(item.title) && item.title === '의무이행사항 점검결과(공중이용시설 위탁운영 시설)'"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      :title="item.subTitle"
      :is-title-dot="item.isTitleDot"
      :tableHeaderTitle="item.tableHeaderTitle || '중대재해처벌법 의무이행 사항'"
      :tableHeaderCheck="item.tableHeaderCheck"
      :tableHeaderNote="item.tableHeaderNote"
      :footer="item.footer"
      v-model="item.list"
      class="mt-3"
      @click="onClickInspectList"
    />

    <!--    파일첨부-->
    <file-upload-module id="fileList" class="w-full mt-4" title="파일첨부" v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly"></file-upload-module>
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule";
import DateSelector from "@/components/selector/DateSelector.vue";
import FieldSelectorMultiple from "@/components/selector/FieldSelectorMultiple.vue";
import FieldSelector from "@/components/selector/FieldSelector.vue";
import DateSelectorRang from "@/components/selector/DateSelectorRang.vue";
import inspectData from "@/views/boards/single-page/1044/inspectData";
import TableCustom from "@/components/TableCustom.vue";
import TableInspectMerge from "@/components/TableInspectMerge.vue";
import PrintRadio from "@/components/print/PrintRadio.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";
import NumberInput from "@/components/NumberInput.vue";
import Print from "./Print.vue";
import { required, minLength } from "vuelidate/lib/validators";

// 선택 불가한 체크리스트를 체크하려 할 때 (실시간 차단)
const INSPECT_BLOCK_MESSAGES = {
  "의무이행사항 점검결과(원료/제조물)": '"해당 시설물의 서식이 아닙니다" 시설물을 직접 설계·제조·표시·유통(판매)하는 경우에는 원료·제조물에 해당합니다.',
  "의무이행사항 점검결과(공중이용시설 직접관리 시설)": '"해당 시설물의 서식이 아닙니다" 해당 시설물은 공중이용시설 중 직접관리 시설입니다.',
  "의무이행사항 점검결과(공중이용시설 위탁운영 시설)": '"해당 시설물의 서식이 아닙니다" 해당 시설물은 공중이용시설 중 위탁운영 시설입니다.',
};
// 선택 가능한데 체크 안 하고 저장 시도할 때 (저장 시점)
const INSPECT_MISSING_MESSAGES = {
  "의무이행사항 점검결과(원료/제조물)": "의무이행사항 점검결과 중 '원료/제조물'을 입력하여 주십시오.",
  "의무이행사항 점검결과(공중이용시설 직접관리 시설)": "의무이행사항 점검결과 중 '공중이용시설(직접관리 시설)'을 입력하여 주십시오.",
  "의무이행사항 점검결과(공중이용시설 위탁운영 시설)": "의무이행사항 점검결과 중 '공중이용시설(위탁관리시설)'을 입력하여 주십시오.",
};

export default {
  components: {
    Print,
    RadioGroup,
    PrintRadio,
    TableInspectMerge,
    TableCustom,
    DateSelectorRang,
    FieldSelector,
    FieldSelectorMultiple,
    DateSelector,
    FileUploadModule,
    FieldSelectModule,
    NumberInput,
  },
  props: {
    isPrint: Boolean,
    header: Object,
    board: Object,
    value: Object,
  },
  validations: {
    form: {
      fields: { required, minLength: minLength(1) },
      점검기간: {
        sDate: { required },
        eDate: { required },
      },
      점검내용: { required },
      총평: { required },
      업무분장_반영여부: { required },
    },
  },
  computed: {
    getTitles() {
      const titleSet = new Set(this.form.inspectList.map((item) => item.title));
      return Array.from(titleSet);
    },
    myInfo() {
      return this.$store.state.auth.userInfo;
    },
    projectId() {
      return this.$route.params.proj_id;
    },
    postId() {
      return parseInt(this.$route.params.postId);
    },
    postList() {
      return [...JSON.parse(JSON.stringify(this.$store.state.board.list))];
    },
    boardId() {
      return this.$route.params.boardId;
    },
    소관부서() {
      if (this.form.fields && this.form.fields.length > 0) {
        const 소관부서 = this.form.fields.map((item) => {
          const meta = item.meta ? JSON.parse(item.meta) : {};
          return meta.소관부서;
        });
        return [...new Set(소관부서)].join(", ");
      }
      return "";
    },

    showWorkplaceInfo() {
      // 사업장정보 안보이게 함
      return parseInt(this.projectId) !== 5;
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
    "form.교육이수현황": {
      immediate: true,
      deep: true,
      handler(value) {
        if (value.length !== 0) {
          this.form.taken = 0;
          this.form.untaken = 0;
          for (const item of this.form.교육이수현황) {
            if (item.take === "이수") {
              this.form.taken += 1;
            } else if (item.take === "미이수") {
              this.form.untaken += 1;
            }
          }
          this.form.takeSum = this.form.taken + this.form.untaken;
        }
      },
    },

    "form.사고발생현황": {
      immediate: true,
      deep: true,
      handler(value) {
        if (value.length !== 0) {
          let sum = 0;
          for (const item of value) {
            if (item.cell1 !== "계") {
              sum += Number(item.cell2);
            }
          }
          value[value.length - 1].cell2 = sum;
        }
      },
    },
  },
  data() {
    return {
      type1Title: ["안전예산 집행내역", "안전인력 운용현황", "안전점검 및 보수보강 등 유지관리 현황", "주요 유해위험 요인 발생 및 조치현황", "비상대피훈련 실시", "관계법령의 의무사항 이행현황", "교육 이수현황"],
      type2Title: ["의무사항 미이행 현황", "사고발생 현황", "기타 개선 및 보완이 필요한 사항"],
      공중이용시설FieldTypes: [
        "building", "bridge", "library", "mixed-use-building", "museum", "complex-facility",
        "water-supply", "indoor-auditorium", "indoor-sports-facility", "daycare", "office",
        "retaining-wall", "medical", "cutting-slope", "dike", "government-building",
        "tunnel", "sewage-treatment", "port", "port-dock",
      ],
      원료제조물FieldTypes: ["drinking-water", "waterworks", "food-processing", "mass_feeding_facility", "lift", "kids-play-area"],
      EnableSave: false,
      showSaveConfirm: false,
      _saveResolve: null,
      editIndex: undefined,
      editData: undefined,
      inspectCheckRenderKey: 0, // 선택 불가 체크 차단 시 vs-checkbox를 강제로 다시 그려 네이티브 checked 표시를 동기화하기 위한 키
      jiphaengNoneRenderKey: 0, // 안전예산 집행내역 해당사항없음 체크 차단 시 vs-checkbox 강제 재렌더링 키
      form: {
        stdInfo: {},
        fields: [], // 시설명
        점검연도: new Date().getFullYear(),
        점검기간: { sDate: "", eDate: "" },
        점검내용: "",
        총평: "",

        type1Checked: [],
        type2Checked: [],

        // 안전예산 집행내역
        year: new Date().getFullYear(),
        구분: "",

        집행내역_직영: [
          { checked: false, 구분: '안전점검', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '보수보강', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '안전조치', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '인건비*', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '기타', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
        ],
        합계_직영: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
        추가소요액확보계획_직영: "",
        집행내역_직영_해당사항없음: false,
        집행내역_위탁: [
          { checked: false, 구분: '안전점검', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '보수보강', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '안전조치', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '인건비*', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
          { checked: false, 구분: '기타', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
        ],
        합계_위탁: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 },
        추가소요액확보계획_위탁: "",
        집행내역_위탁_해당사항없음: false,
        // 안전인력 운용현황
        안전인력_운용현황: [{ cell1: "", cell2: "", cell3: "", cell4: "" }],
        업무분장_반영여부: "",
        업무분장_반영여부_input: "",
        // 안전점검 및 보수보강 등 유지관리 현황
        유지관리_현황_정기점검: [],
        유지관리_현황_수시점검: [],
        유지관리_현황_정밀점검_해당사항없음: false,
        유지관리_현황_정밀점검: [],

        주요유해위험요인발생및조치현황: [],
        주요유해위험요인발생및조치현황_해당사항없음: false,
        비상대피훈련실시: [],
        비상대피훈련실시_해당사항없음: false,
        관계법령의의무사항이행현황: [],
        교육이수현황: [],

        의무사항미이행현황: [],
        의무사항미이행현황_해당사항없음: false,
        사고발생현황: [
          { cell1: "사망자", cell2: "", cell3: "", cell4: "" },
          { cell1: "부상자", cell2: "", cell3: "", cell4: "" },
          { cell1: "질병자", cell2: "", cell3: "", cell4: "" },
          { cell1: "계", cell2: "", cell3: "", cell4: "" },
        ],
        사고발생현황_해당사항없음: false,
        기타개선및보완이필요한사항: "",
        기타개선및보완이필요한사항_해당사항없음: false,

        taken: 0,
        untaken: 0,
        takeSum: 0,

        inspectionTitle: "",
        inspectChecked: [],
        inspectList: inspectData,

        fileList: [],
      },
    };
  },
  methods: {
    async loadListBoard() {
      if (!this.form.stdInfo.field_id) return;

      // 기존에 로드한 데이터가 있으면 그냥 사용
      if (this.oldForm[`${this.form.year}_${this.form.구분}`]) {
        this.EnableSave = false;
        this.form = { ...this.form, 집행내역: this.oldForm[`${this.form.year}_${this.form.구분}`] };
        return;
      } else {
        this.EnableSave = true;
        this.form.집행내역 = {
          안전점검: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 },
          보수보강: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 },
          안전조치: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 },
          인건비: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 },
          기타: { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 },
        };
        this.form.합계 = { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 };
      }

      const params = {
        projectId: this.projectId,
        page: 1,
        fieldId: this.form.stdInfo.field_id,
      };

      const conditions = [
        {
          key: "body.year",
          operator: "=",
          value: `${this.form.year}`,
        },
        {
          key: "body.구분",
          operator: "=",
          value: this.form.구분,
        },
      ];

      for (let i = 0, condition; (condition = conditions[i]); i++) {
        const keys = Object.keys(condition);
        for (const key of keys) {
          params[`conditions[${i}][${key}]`] = condition[key];
        }
      }

      HttpRequest.get(`/api/v1/board/post/${this.boardId}`, { params })
        .then((response) => {
          if (response.data.length < 1) {
            this.EnableSave = true;
          } else {
            this.EnableSave = false;

            const data = this.getContent(response.data[0]);
            if (data.집행내역) {
              const keys = Object.keys(data.집행내역);
              if (keys.indexOf("기타") === -1) {
                data.집행내역["기타"] = { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0, 집행률: 0 };
              }
            }

            this.oldForm = { ...this.oldForm, [`${this.form.year}_${this.form.구분}`]: data.집행내역 };
            this.form.집행내역 = data.집행내역;
          }
        })
        .catch((err) => {});
    },
    recalcJiphaeng(type) {
      const key = type === '직영' ? '집행내역_직영' : '집행내역_위탁';
      const sumKey = type === '직영' ? '합계_직영' : '합계_위탁';
      const rows = this.form[key];
      rows.forEach(function(row) {
        row.잔액 = Number(row.편성액) - Number(row.집행액);
      });
      const sum = { 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 };
      rows.forEach(function(row) {
        sum.편성액 += Number(row.편성액) || 0;
        sum.집행액 += Number(row.집행액) || 0;
        sum.잔액 += Number(row.잔액) || 0;
        sum.추가소요액 += Number(row.추가소요액) || 0;
      });
      this.form[sumKey] = sum;
    },
    addJiphaengRow(type) {
      const key = type === '직영' ? '집행내역_직영' : '집행내역_위탁';
      this.form[key].push({ checked: false, 구분: '', 편성액: 0, 집행액: 0, 잔액: 0, 추가소요액: 0 });
    },
    deleteJiphaengRows(type) {
      const key = type === '직영' ? '집행내역_직영' : '집행내역_위탁';
      this.form[key] = this.form[key].filter(function(row) { return !row.checked; });
      this.recalcJiphaeng(type);
    },
    // 안전예산 집행내역 - 관리방법상 필수인 시설군(직영/위탁)의 "해당사항 없음" 체크를 막고 안내팝업 표출
    onToggleJiphaengNone(type) {
      const key = type === '직영' ? '집행내역_직영_해당사항없음' : '집행내역_위탁_해당사항없음';
      if (!this.form[key]) {
        const 관리방법 = type === '직영' ? '직접관리' : '위탁관리';
        const fields = Array.isArray(this.form.fields) ? this.form.fields : [];
        const required = fields.some((field) => field.관리형태 === 관리방법);
        if (required) {
          const label = type === '직영' ? '직영시설' : '위탁시설';
          window.dispatchEvent(new CustomEvent("requiredFieldAlert", {
            detail: {
              messages: [{
                text: `[안전예산 집행내역] ${label}의 안전예산 집행내역을 입력하여 주십시오.`,
                sub: "※ 주의: 안전예산 집행내역은 위탁시설과 직영시설을 분리해서 입력해 주십시오.",
              }],
            },
          }));
          this.jiphaengNoneRenderKey++;
          return;
        }
      }
      this.form[key] = !this.form[key];
    },
    selectFields(fields) {
      if (this.form.fields && this.form.fields.length > 0) {
        this.form.fields = this.form.fields.map((item) => {
          const meta = item.meta ? JSON.parse(item.meta) : {};

          if (!item.관리형태) {
            item.관리형태 = meta.관리방법 || "-";
          }

          if (!item.시설종류) {
            item.시설종류 = `${meta.cat1 || "-"} > ${meta.cat2 || "-"} > ${meta.cat3 || "-"} > ${meta.classification || "-"}`;
          }

          return {
            ...item,
            규모: "",
            대상법령: "",
            위탁부서: "",
            위탁기간: { sDate: "", eDate: "" },
            수탁기관명: "",
            수탁비용: "",
          };
        });
      }
      this.applyInspectCheckedByFields();
    },
    // 시설명(form.fields) 기준으로 기대되는 inspectChecked 타입 Set 반환
    getExpectedInspectTypes() {
      const types = new Set();
      const fields = Array.isArray(this.form.fields) ? this.form.fields : [];
      for (const field of fields) {
        const meta = field.meta ? JSON.parse(field.meta) : {};
        const fieldType = field.field_type || meta.field_type;
        if (!fieldType) continue;

        const is원료제조물 = this.원료제조물FieldTypes.includes(fieldType);
        const is공중이용시설 = this.공중이용시설FieldTypes.includes(fieldType);
        if (!is원료제조물 && !is공중이용시설) continue;

        if (is원료제조물 && Number(meta.실질적성격) === 1) {
          // 시설물의 실질적 성격이 "원료·제조물"인 경우에만 4-1 서식 대상
          types.add("의무이행사항 점검결과(원료/제조물)");
          continue;
        }
        // 실질적 성격이 공중이용시설이거나(실질적성격=0), 애초에 공중이용시설로 분류된 시설물
        types.add(meta.관리방법 === "위탁관리" ? "의무이행사항 점검결과(공중이용시설 위탁운영 시설)" : "의무이행사항 점검결과(공중이용시설 직접관리 시설)");
      }
      return types;
    },
    applyInspectCheckedByFields() {
      const expected = this.getExpectedInspectTypes();
      const previouslyChecked = Array.isArray(this.form.inspectChecked) ? this.form.inspectChecked : [];
      const removed = previouslyChecked.filter((title) => !expected.has(title));
      removed.forEach((title) => this.resetInspectListData(title));
      this.form.inspectChecked = Array.from(expected);
    },
    // 체크 해제된(더 이상 해당하지 않는) 체크리스트의 입력값(양호/미흡/해당없음, 비고)을 초기화
    resetInspectListData(title) {
      const inspectList = Array.isArray(this.form.inspectList) ? this.form.inspectList : [];
      for (const item of inspectList) {
        if (item.title !== title || !Array.isArray(item.list)) continue;
        for (const row of item.list) {
          if (!row) continue;
          row.isChecked = "";
          row.is_checked = "";
          row["비고(보완계획)"] = "";
        }
      }
    },
    // 의무이행사항 점검결과 체크박스 클릭 처리 - 해당 시설물에 유효하지 않은 서식은 체크를 막고 안내팝업 표출
    onToggleInspectChecked(title) {
      const idx = this.form.inspectChecked.indexOf(title);
      if (idx > -1) {
        // 수동 해제는 항상 허용 - 시설 구성이 바뀐 게 아니므로 입력값은 지우지 않는다
        // (시설 변경으로 인한 자동 해제 시에만 applyInspectCheckedByFields()에서 초기화)
        this.form.inspectChecked.splice(idx, 1);
        return;
      }
      const expected = this.getExpectedInspectTypes();
      if (expected.size > 0 && !expected.has(title)) {
        const messages = Object.keys(INSPECT_BLOCK_MESSAGES)
          .filter((t) => expected.has(t))
          .map((t) => ({ text: INSPECT_BLOCK_MESSAGES[t] }));
        if (messages.length) {
          window.dispatchEvent(new CustomEvent("requiredFieldAlert", { detail: { messages } }));
        }
        // vs-checkbox는 checked prop이 없어, 브라우저가 먼저 토글해버린 네이티브 체크 표시를
        // Vue가 "변경 없음"으로 보고 되돌리지 않는다 - key를 바꿔 강제로 다시 그려서 동기화한다
        this.inspectCheckRenderKey++;
        return;
      }
      this.form.inspectChecked.push(title);
    },
    onClickEditData(index) {
      this.editIndex = index;
      this.editData = JSON.parse(JSON.stringify(this.form.fields[index]));
    },
    onClickRemoveData(index) {
      this.form.fields.splice(index, 1);
      this.applyInspectCheckedByFields();
    },
    onClickFinishData(index) {
      this.form.fields[index] = this.editData;
      this.editIndex = null;
      this.editData = null;
    },
    onClickCancelData(index) {
      this.editIndex = null;
      this.editData = null;
    },

    onClickInspectList(key, value) {
      if (key === "유해·위험요인 점검") {
        const popup = window.open(`/project/${this.projectId}/tab/613`, "_blank", `width=${screen.width},height=${screen.height}`);
      }
    },

    validate(options = {}) {
      // 임시저장(draft) 시에는 필수 검증을 생략하고 저장될 수 있도록 한다
      if (options && options.isDraft) {
        return true;
      }

      this.$v.$touch();

      const self = this;
      const getEl = function(refName) {
        const ref = self.$refs[refName];
        if (!ref) return null;
        return ref.$el || ref;
      };
      const scrollTo = function(refName) {
        return function() {
          const el = getEl(refName);
          if (!el) return;
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function() {
            const focusable = el.querySelector('input:not([type="hidden"]), textarea');
            if (focusable) focusable.focus();
          }, 400);
        };
      };
      const scrollAndClick = function(refName) {
        return function() {
          const el = getEl(refName);
          if (!el) return;
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function() {
            const clickable = el.querySelector('input:not([type="hidden"]), [role="combobox"], button, .vs-select--input');
            if (clickable) clickable.click();
            else el.click();
          }, 400);
        };
      };

      const messages = [];

      if (!this.form.점검연도) {
        messages.push({ text: '[연도]를 입력하여 주십시오.', action: scrollTo('refYeong') });
      }
      if (!this.form.구분) {
        messages.push({ text: '[구분]의 해당 점검에 체크하여 주십시오.', action: scrollTo('refGubun') });
      }
      if (!this.$v.form.fields.required || !this.$v.form.fields.minLength) {
        messages.push({ text: '[점검시설정보] "시설명"을(를) 입력하여 주십시오.', action: scrollAndClick('refSiseol') });
      }
      if (!this.$v.form.점검기간.sDate.required || !this.$v.form.점검기간.eDate.required) {
        messages.push({ text: '[점검시설정보] "점검기간"을(를) 입력하여 주십시오.', action: scrollAndClick('refGigan') });
      }
      if (!this.$v.form.점검내용.required) {
        messages.push({ text: '[점검시설정보] "점검내용"을(를) 입력하여 주십시오.', action: scrollTo('refJeomgum') });
      }
      if (!this.$v.form.총평.required) {
        messages.push({ text: '[점검시설정보] "총평"을(를) 입력하여 주십시오.', action: scrollTo('refChongpyong') });
      }

      // 안전예산 집행내역 - 선택한 시설의 관리방법(직접관리/위탁관리)에 따라 직영/위탁 각각 필수 입력
      const jiphaengFields = Array.isArray(this.form.fields) ? this.form.fields : [];
      const 직영필요 = jiphaengFields.some((field) => field.관리형태 === '직접관리');
      const 위탁필요 = jiphaengFields.some((field) => field.관리형태 === '위탁관리');
      const 직영값있음 = Array.isArray(this.form.집행내역_직영) && this.form.집행내역_직영.some(function(row) {
        return Number(row.편성액) !== 0 || Number(row.집행액) !== 0 || Number(row.추가소요액) !== 0;
      });
      const 위탁값있음 = Array.isArray(this.form.집행내역_위탁) && this.form.집행내역_위탁.some(function(row) {
        return Number(row.편성액) !== 0 || Number(row.집행액) !== 0 || Number(row.추가소요액) !== 0;
      });
      if (직영필요 && !this.form.집행내역_직영_해당사항없음 && !직영값있음) {
        messages.push({
          text: '[안전예산 집행내역] 직영시설의 안전예산 집행내역을 입력하여 주십시오.',
          sub: "※ 주의: 안전예산 집행내역은 위탁시설과 직영시설을 분리해서 입력해 주십시오.",
          action: scrollTo('refJiphaeng'),
        });
      }
      if (위탁필요 && !this.form.집행내역_위탁_해당사항없음 && !위탁값있음) {
        messages.push({
          text: '[안전예산 집행내역] 위탁시설의 안전예산 집행내역을 입력하여 주십시오.',
          sub: "※ 주의: 안전예산 집행내역은 위탁시설과 직영시설을 분리해서 입력해 주십시오.",
          action: scrollTo('refJiphaeng'),
        });
      }
      if (!직영필요 && !위탁필요 && !직영값있음 && !위탁값있음) {
        messages.push({ text: '[안전예산 집행내역] "안전예산 집행내역" 항목을 1개 이상 입력하여 주십시오.', action: scrollTo('refJiphaeng') });
      }

      // 안전인력 운용현황 - 인력운용 총괄: 각 행의 확보인원(cell1), 주요업무(cell2) 필수
      const 인력운용총괄유효 =
        Array.isArray(this.form.안전인력_운용현황) &&
        this.form.안전인력_운용현황.length > 0 &&
        this.form.안전인력_운용현황.every(
          (row) =>
            row.cell1 !== undefined &&
            row.cell1 !== null &&
            String(row.cell1).trim() !== "" &&
            row.cell2 !== undefined &&
            row.cell2 !== null &&
            String(row.cell2).trim() !== ""
        );
      if (!인력운용총괄유효) {
        messages.push({ text: '[안전인력 운용현황] "인력운용 총괄"의 "확보인원"과 "주요업무"를 모두 입력하여 주십시오.', action: scrollTo('refInryeok') });
      }
      if (!this.$v.form.업무분장_반영여부.required) {
        messages.push({ text: '[안전인력 운용현황] "업무분장 반영 여부"를 체크하여 주십시오.', action: scrollTo('refBuummu') });
      }

      const 행에셀값있음 = (rows, cellKeys) =>
        Array.isArray(rows) &&
        rows.some((row) =>
          row && cellKeys.every((k) => row[k] !== undefined && row[k] !== null && String(row[k]).trim() !== "")
        );

      // 정기점검 - 관련법령(cell1)~점검내용(cell4) 작성된 행 1개 이상
      if (!행에셀값있음(this.form.유지관리_현황_정기점검, ["cell1", "cell2", "cell3", "cell4"])) {
        messages.push({ text: '[안전점검 및 보수보강 등 유지관리 현황] "정기점검" 사항을 입력하여 주십시오.', action: scrollTo('refJeonggi') });
      }
      // 수시점검(유지관리) - 필수, 모든 셀 미작성 시
      if (!행에셀값있음(this.form.유지관리_현황_수시점검, ["cell1", "cell2", "cell3", "cell4"])) {
        messages.push({
          text: '[안전점검 및 보수보강 등 유지관리 현황] "수시점검(유지관리)" 사항을 입력하여 주십시오.',
          action: scrollTo('refSusi'),
        });
      }
      // 정밀안전 점검 및 진단 - 해당사항없음 미체크 + 미작성 시
      if (!this.form.유지관리_현황_정밀점검_해당사항없음 && !행에셀값있음(this.form.유지관리_현황_정밀점검, ["cell1", "cell2", "cell3", "cell4"])) {
        messages.push({
          text: '[안전점검 및 보수보강 등 유지관리 현황] "정밀안전 점검 및 진단" 사항을 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refJeongmil'),
        });
      }

      // 주요 유해위험 요인 발생 및 조치현황 - 해당사항없음 미체크 + 모든 셀 미작성 시
      if (
        !this.form.주요유해위험요인발생및조치현황_해당사항없음 &&
        !행에셀값있음(this.form.주요유해위험요인발생및조치현황, ["cell1", "cell2", "cell3", "cell4"])
      ) {
        messages.push({
          text: '[주요 유해위험 요인 발생 및 조치현황] "주요 유해위험 요인 발생 및 조치현황"사항을 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refYuhae'),
        });
      }

      // 비상대피훈련 실시 - 해당사항없음 미체크 + 대상법령(cell1)/시행여부(cell2) 미작성 시
      if (
        !this.form.비상대피훈련실시_해당사항없음 &&
        !행에셀값있음(this.form.비상대피훈련실시, ["cell1", "cell2"])
      ) {
        messages.push({
          text: '[비상대피훈련 실시] "비상대피훈련 실시"사항을 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refBisang'),
        });
      }

      // 관계법령의 의무사항 이행현황 - 필수, 의무사항(cell1)/이행여부(cell2) 작성된 행 1개 이상
      if (!행에셀값있음(this.form.관계법령의의무사항이행현황, ["cell1", "cell2"])) {
        messages.push({
          text: '[관계법령의 의무사항 이행현황] "관계법령의 의무사항 이행현황"을 입력하여 주십시오.',
          action: scrollTo('refGwangyue'),
        });
      }

      // 교육 이수현황 - 필수, 교육대상(target)/이수여부(take)/이수일(plan) 작성된 행 1개 이상
      if (!행에셀값있음(this.form.교육이수현황, ["target", "take", "plan"])) {
        messages.push({
          text: '[교육 이수현황] "교육 이수현황"을 입력하여 주십시오.',
          action: scrollTo('refGyoyuk'),
        });
      }

      // 의무사항 미이행 현황 - 해당사항없음 미체크 + 모든 셀 미작성 시
      if (
        !this.form.의무사항미이행현황_해당사항없음 &&
        !행에셀값있음(this.form.의무사항미이행현황, ["cell1", "cell2", "cell3", "cell4"])
      ) {
        messages.push({
          text: '[의무사항 미이행 현황] "의무사항 미이행 현황"을 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refUimu'),
        });
      }

      // 사고발생 현황 - 해당사항없음 미체크. 사망자/부상자/질병자 중 하나라도 피해인원 입력 시 해당 행의 사고경위및조치(cell3)도 필수
      const 사고발생현황행 = Array.isArray(this.form.사고발생현황)
        ? this.form.사고발생현황.filter((row) => row && row.cell1 !== "계")
        : [];
      if (!this.form.사고발생현황_해당사항없음) {
        const 입력된행있음 = 사고발생현황행.some((row) => row && row.cell2 !== undefined && row.cell2 !== null && String(row.cell2).trim() !== "" && Number(row.cell2) !== 0);
        if (입력된행있음) {
          const 조치미입력행있음 = 사고발생현황행.some((row) => {
            const has피해 = row.cell2 !== undefined && row.cell2 !== null && String(row.cell2).trim() !== "" && Number(row.cell2) !== 0;
            const no조치 = !row.cell3 || String(row.cell3).trim() === "";
            return has피해 && no조치;
          });
          if (조치미입력행있음) {
            messages.push({
              text: '[사고발생 현황] 피해인원이 입력된 행의 "사고경위 및 조치"를 입력하여 주십시오.',
              action: scrollTo('refSago'),
            });
          }
        } else {
          messages.push({
            text: '[사고발생 현황] "사고발생 현황"을 입력하여 주십시오.',
            sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
            action: scrollTo('refSago'),
          });
        }
      }

      // 기타 개선 및 보완이 필요한 사항 - 해당사항없음 미체크 + 점검결과(총평) 미입력 시
      if (
        !this.form.기타개선및보완이필요한사항_해당사항없음 &&
        (!this.form.기타개선및보완이필요한사항 || String(this.form.기타개선및보완이필요한사항).trim() === "")
      ) {
        messages.push({
          text: '[기타 개선 및 보완이 필요한 사항] "점검결과(총평)"을 입력하여 주십시오.',
          sub: "해당사항이 없을 경우 '해당사항 없음'에 체크하여 주십시오.",
          action: scrollTo('refGita'),
        });
      }
      // 의무이행사항 점검결과 - 시설명 기반 자동 분류값(expected)과 실제 선택(actual) 비교
      const expectedInspectTypes = this.getExpectedInspectTypes();
      const actualInspectTypes = new Set(Array.isArray(this.form.inspectChecked) ? this.form.inspectChecked : []);
      if (actualInspectTypes.size === 0) {
        messages.push({ text: '[의무이행사항 점검결과] 해당하는 "의무이행사항 점검결과"에 체크하고 결과를 입력하여 주십시오.', action: scrollTo('refInspect') });
      } else {
        // expected에 있지만 actual에 없는 항목 → 선택 가능한데 체크하지 않은 항목 안내
        if (
          expectedInspectTypes.has("의무이행사항 점검결과(원료/제조물)") &&
          !actualInspectTypes.has("의무이행사항 점검결과(원료/제조물)")
        ) {
          messages.push({ text: INSPECT_MISSING_MESSAGES["의무이행사항 점검결과(원료/제조물)"] });
        }
        if (
          expectedInspectTypes.has("의무이행사항 점검결과(공중이용시설 직접관리 시설)") &&
          !actualInspectTypes.has("의무이행사항 점검결과(공중이용시설 직접관리 시설)")
        ) {
          messages.push({ text: INSPECT_MISSING_MESSAGES["의무이행사항 점검결과(공중이용시설 직접관리 시설)"] });
        }
        if (
          expectedInspectTypes.has("의무이행사항 점검결과(공중이용시설 위탁운영 시설)") &&
          !actualInspectTypes.has("의무이행사항 점검결과(공중이용시설 위탁운영 시설)")
        ) {
          messages.push({ text: INSPECT_MISSING_MESSAGES["의무이행사항 점검결과(공중이용시설 위탁운영 시설)"] });
        }
      }

      // 체크된 의무이행사항 점검결과 - 각 체크리스트 행이 양호/미흡/해당없음 중 하나가 선택되어야 함
      const 불완전점검표명 = new Set();
      const inspectList = Array.isArray(this.form.inspectList) ? this.form.inspectList : [];
      for (const item of inspectList) {
        if (!item || !actualInspectTypes.has(item.title) || !Array.isArray(item.list)) continue;
        const 미체크행있음 = item.list.some(
          (row) => !row || row.isChecked === undefined || row.isChecked === null || String(row.isChecked).trim() === ""
        );
        if (미체크행있음) {
          const match = (item.title || "").match(/\(([^)]+)\)/);
          let 점검표명 = match ? match[1] : item.title || "";
          if (item.subTitle) {
            점검표명 += ` ${String(item.subTitle).replace(/^-\s*/, "")}`;
          }
          불완전점검표명.add(점검표명);
        }
      }
      for (const 점검표명 of 불완전점검표명) {
        messages.push({ text: `[의무이행사항 점검결과] "의무이행사항 점검결과 중 '${점검표명}'에 체크되지 않은 항목이 있습니다."`, action: scrollTo('refInspect') });
      }

      if (messages.length > 0) {
        window.dispatchEvent(new CustomEvent("requiredFieldAlert", { detail: { messages } }));
        return false;
      }

      return new Promise((resolve) => {
        this._saveResolve = resolve;
        this.showSaveConfirm = true;
      });
    },

    onConfirmSave() {
      this.showSaveConfirm = false;
      if (this._saveResolve) {
        this._saveResolve(true);
        this._saveResolve = null;
      }
    },

    onCancelSave() {
      this.showSaveConfirm = false;
      if (this._saveResolve) {
        this._saveResolve(false);
        this._saveResolve = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$border: 1px solid #cdcdcd;
input {
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  padding: 0.7rem !important;
  transition: all 0.3s ease;
  &:focus {
    border: 1px solid rgba(var(--vs-primary), 1) !important;
    box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.15);
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 0.5rem;
  }

  th {
    height: 50px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    background-color: #f9f9f9;
  }
}
.icon-buttons {
  button {
    width: 32px;
    height: 32px;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #7e72f2;
    background: none;
    border: 1px solid #7e72f2;
    border-radius: 4px;
    cursor: pointer;

    span {
      margin: 0;
    }
  }
}

.section-subtitle {
  border-radius: 5px;
  border: 1px solid #cdcdcd;
  background: #1e0d61;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 16px;
}

.required-notice {
  border-radius: 5px;
  border: 1px solid #cdcdcd;
  padding: 12px 16px;
  font-size: 14px;
  color: #333;

  &::before {
    content: "* ";
    color: #ff0000;
    font-weight: 600;
  }
}

.required::before {
  content: "*";
  color: #ff0000;
  font-weight: 600;
  margin-right: 4px;
}
</style>
